import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/lib/studioApi";
import { marketCopy } from "./copy";
import { useLanguage } from "@/i18n";

export const FALLBACK_COUNTRY = { country: "BE", name: "Belgique", flag: "🇧🇪", currency: "EUR", currency_symbol: "€", rates: [21, 12, 6, 0], standard_rate: 21, online_payment: "mollie", einvoicing: "peppol", subscription: { amount: 25, currency: "EUR" }, tax_ids: [] };
const STORAGE = "devisto_country";

/* Par défaut le site reste belge (Peppol) ; un autre pays n'est affiché que si le visiteur le choisit lui-même. */
const initialCountry = () => (localStorage.getItem(`${STORAGE}_chosen`) ? localStorage.getItem(STORAGE) : null) || "BE";

const MarketContext = createContext({ country: "BE", setCountry: () => {}, market: FALLBACK_COUNTRY, countries: [FALLBACK_COUNTRY], copy: marketCopy("BE") });

/* Espagne ⇔ espagnol : choisir l'Espagne traduit tout le site, choisir l'espagnol sélectionne l'Espagne. */
export const MarketProvider = ({ children }) => {
  const { lang, setLang } = useLanguage();
  const [countries, setCountries] = useState([FALLBACK_COUNTRY]);
  const [country, setCountryState] = useState(initialCountry);

  useEffect(() => {
    if (lang === "es" && country !== "ES") { localStorage.setItem(STORAGE, "ES"); localStorage.setItem(`${STORAGE}_chosen`, "1"); setCountryState("ES"); }
  }, [lang, country]);

  useEffect(() => {
    axios.get(`${API}/fiscal/countries`).then(({ data }) => { if (data.countries?.length) setCountries(data.countries); }).catch(() => {});
  }, []);

  const setCountry = useCallback((c) => {
    localStorage.setItem(STORAGE, c); localStorage.setItem(`${STORAGE}_chosen`, "1"); setCountryState(c);
    if (c === "ES") setLang("es");
    else if (lang === "es") setLang("fr");
  }, [lang, setLang]);

  const value = useMemo(() => {
    const market = countries.find((c) => c.country === country) || countries[0] || FALLBACK_COUNTRY;
    return { country: market.country, setCountry, market, countries, copy: marketCopy(market.country) };
  }, [country, countries, setCountry]);

  return <MarketContext.Provider value={value}>{children}</MarketContext.Provider>;
};

export const useMarket = () => useContext(MarketContext);

const fmtPct = (v) => String(v).replace(".", ",");
/* « TVA 21 % / 12 % / 6 % » construit depuis les taux configurés par l'admin, jamais codés en dur. */
const VAT_WORD = { fr: "TVA", es: "IVA", nl: "Btw" };
export const vatLine = (market, lang = "fr") => {
  const rates = (market.rates || []).filter((r) => r > 0);
  const word = VAT_WORD[lang] || "TVA";
  return rates.length ? `${word} ${rates.map(fmtPct).join(" % / ")} %` : word;
};
export const priceLabel = (market) => {
  const s = market.subscription || {};
  const amount = s.amount ?? 25;
  return `${amount}\u00a0${market.currency_symbol || "€"}`;
};
export const moneySample = (v, market) => `${v}\u00a0${market.currency_symbol || "€"}`;
